import React, { useContext, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button, Header, Loader, Modal,
} from 'semantic-ui-react';
import MemberSelections from '../Projects/ViewProject/MemberSelections';
import { SHARE_CHAT_SESSION } from '../../queries/chat';
import { TEAM_MEMBERS } from '../../queries/team';
import DataContext from '../../contexts/DataContext';
import MessageContext from '../../contexts/MessageContext';

// Shares a team or global chat session read-only. A team session can be
// shared with members of the current team. A global session searches every
// team's data, so it can only be shared with other superusers (Dexter team
// admins). The API enforces both rules.
const ShareChatModal = ({ session, onShared }) => {
  const { user } = useContext(DataContext);
  const { setSuccessPopup, setErrorPopup } = useContext(MessageContext);
  const [open, setOpen] = useState(false);
  const [selections, setSelections] = useState([]);

  const isGlobal = session.scopeType === 'global';
  const dexterTeamId = user.membership.find((m) => m.teamName === 'Dexter')?.teamId;
  const { data: dexterTeamData, loading: membersLoading } = useQuery(TEAM_MEMBERS, {
    variables: { id: dexterTeamId },
    skip: !open || !isGlobal || !dexterTeamId,
  });
  const superUserMembers = (dexterTeamData?.team?.members || []).filter((m) => m.role === 'Admin');

  const [shareChatSession, { loading }] = useMutation(SHARE_CHAT_SESSION, {
    onCompleted: (result) => {
      setOpen(false);
      setSuccessPopup({
        title: 'Chat sharing updated',
        content: 'The selected people can now view this chat.',
      });
      if (onShared) onShared(result.shareChatSession);
    },
    onError: (error) => setErrorPopup({
      title: 'Unable to share chat',
      content: error.message,
    }),
  });

  return (
    <Modal
      closeIcon
      open={open}
      onOpen={() => {
        setSelections((session.sharedWith || []).map((u) => u.id));
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      trigger={<Button basic size="tiny" icon="share alternate" content="Share" />}
    >
      <Modal.Header>Share chat</Modal.Header>
      <Modal.Content>
        <Header as="h5">
          {isGlobal
            ? 'Select superusers who can view this chat. They can read it but not send messages.'
            : 'Select teammates who can view this chat. They can read it but not send messages.'}
        </Header>
        {isGlobal && membersLoading ? <Loader active inline="centered" /> : (
          <MemberSelections
            selections={selections}
            setSelections={setSelections}
            exclusions={[user.id]}
            members={isGlobal ? superUserMembers : undefined}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button className="basic-btn" onClick={() => setOpen(false)}>Cancel</Button>
        <Button
          className="primary-btn"
          loading={loading}
          disabled={loading}
          onClick={() => shareChatSession({
            variables: {
              sessionId: session.id,
              userIds: selections.filter((id) => id !== user.id),
            },
          })}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ShareChatModal;
